import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';

import { ButtonUI, DataGridIdentifiers, DialogUI, DatagridPremiumUi } from '../Interface';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import itemService from '../../service/item.service';
import { formatCurrency } from '../../utils/currencyHelper';
import { dataGridPremiumColumns } from '../../utils/helper';

const NEW_STOCK_DIALOG_PARAM = 'ITEM_STOCK_DIALOG_NEW_HEADER';

const getDisplayValue = (value, quantityUnitCode) => {
  if (value === null || value === undefined || value === '') return '-';

  return quantityUnitCode ? `${value} ${quantityUnitCode}` : value;
};

const InfoField = ({ label, value, labelXs = 4, valueXs = 8 }) => (
  <Grid container spacing={0.5} alignItems="center">
    <Grid item xs={labelXs}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
    </Grid>
    <Grid item xs={valueXs}>
      <Typography variant="body1" color="text.secondary">
        {value ?? '-'}
      </Typography>
    </Grid>
  </Grid>
);

const ItemStockDialog = ({
  open,
  onClose = () => {},
  data,
  showPrice = true,
  stockHeaderData = null,
  useNewHeader: useNewHeaderOverride,
}) => {
  const { getConfig, config } = useConfig();
  const { params } = useParam();

  const [priceData, setPriceData] = useState({});
  const [rows, setRows] = useState([]);
  const [openQuantitiesData, setOpenQuantitiesData] = useState({});

  const useNewHeader = useNewHeaderOverride ?? params?.[NEW_STOCK_DIALOG_PARAM] === 'Y';
  const quantityUnitCode = data?.quantityUnit?.code ?? openQuantitiesData?.quantityUnitCode ?? '';
  const headerData = stockHeaderData ?? openQuantitiesData ?? {};

  const getData = async () => {
    if (!data?.itemCode) return;

    const requests = [itemService.getItemStock(data.itemCode)];
    const shouldRequestOpenQuantities = useNewHeader && !stockHeaderData;
    const shouldRequestPrice = useNewHeader || showPrice;

    if (shouldRequestOpenQuantities) requests.push(itemService.getOpenQuantities(data.itemCode));
    if (shouldRequestPrice) requests.push(itemService.getCalculatedPrice(data.itemCode));

    const responses = await Promise.all(requests);
    const stockRes = responses[0];
    const openQuantitiesResponse = shouldRequestOpenQuantities ? responses[1] : null;
    const priceResponse = shouldRequestPrice ? responses[responses.length - 1] : null;

    setRows(stockRes ?? []);
    setPriceData(priceResponse ?? {});
    setOpenQuantitiesData(shouldRequestOpenQuantities ? openQuantitiesResponse ?? {} : {});
  };

  useEffect(() => {
    if (Object.keys(config).length === 0) getConfig();
  }, [config, getConfig]);

  useEffect(() => {
    if (open) {
      getData();
    } else {
      setPriceData({});
      setRows([]);
      setOpenQuantitiesData({});
    }
  }, [open, data?.itemCode, useNewHeader, showPrice, stockHeaderData]);

  const columns = dataGridPremiumColumns([
    {
      field: 'itemCode',
      headerName: 'Cikkszám',
      type: 'string',
    },
    {
      field: 'whsCode',
      headerName: 'Raktár',
      type: 'string',
      groupable: true,
    },
    {
      field: 'binCode',
      headerName: 'Rakhely',
      type: 'string',
      groupable: true,
    },
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      type: 'string',
      groupable: true,
    },
    {
      field: 'effectiveQty',
      headerName: 'Készlet',
      type: 'number',
      renderCell: (cellParams) => (
        <Typography variant="body1" color="text.secondary">
          {cellParams.value} {quantityUnitCode}
        </Typography>
      ),
    },
  ]);

  const oldHeaderContent =
    showPrice &&
    (
      <>
        <Grid item xs={12} md={6}>
          <InfoField
            label="Mérlegelt átlagár:"
            value={priceData?.currentWac ? `${formatCurrency(priceData.currentWac, 2)} ${config.currencyCode}` : '-'}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoField
            label="Utolsó beszerzési ár:"
            value={
              priceData?.lastPurchasePrice ? `${formatCurrency(priceData.lastPurchasePrice, 0)} ${config.currencyCode}` : '-'
            }
          />
        </Grid>
      </>
    );

  const newHeaderContent = (
    <>
      <Grid item xs={12} md={6}>
        <InfoField label="Megrendelt:" value={getDisplayValue(headerData?.openPurchaseQty, quantityUnitCode)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <InfoField label="Gyártás alatt:" value={getDisplayValue(headerData?.inProductionQty, quantityUnitCode)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <InfoField label="Vevői rendelés alatt:" value={getDisplayValue(headerData?.openSalesQty, quantityUnitCode)} />
      </Grid>
      <Grid item xs={12} md={6}>
        <InfoField label="Foglalt mennyiség:" value={getDisplayValue(headerData?.reservedQty, quantityUnitCode)} />
      </Grid>
    </>
  );

  const headerContent = useNewHeader ? newHeaderContent : oldHeaderContent;

  return (
    <DialogUI open={open} onClose={onClose} headerContent="Készlet információ" maxWidth="lg" fullWidth>
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={1} mb={2} sx={{ background: '#E9F5FE', borderRadius: '4px', p: '0.5rem' }}>
          <Grid item xs={12} md={6}>
            <InfoField label="Cikkszám:" value={data?.itemCode ?? openQuantitiesData?.itemCode} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoField label="Készleten:" value={getDisplayValue(priceData?.onHand, quantityUnitCode)} />
          </Grid>
          <Grid item xs={12}>
            <InfoField label="Cikknév:" value={data?.itemName ?? openQuantitiesData?.itemName} labelXs={2} valueXs={10} />
          </Grid>
          {headerContent}
        </Grid>

        <DatagridPremiumUi
          datagridIdentifier={DataGridIdentifiers.ItemStockTable}
          height={480}
          columns={columns}
          rows={rows}
          getRowHeight={() => 'auto'}
        />
      </Box>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <ButtonUI text="Bezár" className="bg-success" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default ItemStockDialog;
